import React from "react";
import DesignContextUtils from "./DesignContextUtils";
import { CUSTOM, GOOGLE, TYPEKIT } from "../constants/FontTypes";
import { ASSET_URL_OP_FILE } from "../constants/DesignContextConstants";

/**
 * Joins string of formatted fonts and appends it to the googlefonts url
 * @param {string[]} fonts
 * @returns {string}
 */
export const buildGoogleFontsURL = (fonts) =>
  `https://fonts.googleapis.com/css?family=${fonts.join("|")}`;

/**
 * Removes characters that aren't letters or spaces
 * then replaces multiple spaces with single spaces and trims what's left
 * @param {string} fontName
 * @returns
 */
export const formatFontName = (fontName) =>
  fontName.replace(/[^A-Za-z ]/g, "").replace(/\s{2,}/g, " ");

/**
 * A font configuration
 * @typedef Font
 * @type {Object}
 * @prop {String} family
 * @prop {Number=} weight
 * @prop {String=} style
 * @prop {String=} src
 * @prop {String} type
 */

/**
 * Takes in a url getter and returns the body of generateRenderableFonts.
 * @param {function(src, op)} urlGetter - A function that generates a designAssetUrl.
 * @returns
 */

export const generateFontStrings =
  (urlGetter) =>
  /**
   * Generates font strings based on params in the font object and reduces them to a list and a string.
   * @param {Font[]} fonts - A list of font objects.
   * @returns {[string[], string]}
   */
  (fonts) =>
    fonts.reduce(
      (acc, font) => {
        /* eslint-disable-next-line prefer-const */
        let [googleFonts, customFonts] = acc;
        const { family, src, type } = font;

        if (GOOGLE === type) {
          const vals = `${font?.weight || ""}${font?.style || ""}`;
          googleFonts.push(`${family.replace(/\s+/g, "+")}:${vals}`);
        } else if (CUSTOM === type) {
          const url = urlGetter({ key: src, op: ASSET_URL_OP_FILE });
          customFonts += `@font-face{font-family:"${family}";src:url("${url}");}`;
        } else if (TYPEKIT === type) {
          customFonts += `@font-face{font-family:"${family}";}`;
        }

        return [googleFonts, customFonts];
      },
      [[], ""]
    );

/**
 *  Generates link tags for Google fonts and a style tag for custom fonts and returns them in a single array.
 * @param {Font[]} fonts - The list of font objects to be turned into tags
 * @param {Map} designContext - The design context immutable map containing the assetUrl
 * @returns {(HTMLLinkElement|HTMLStyleElement)[]}
 */
export const generateFontsTagsFromDesignContext = (fonts, designContext) => {
  const tags = [];
  const { generateDesignAssetUrl } = DesignContextUtils.fromContext(designContext);
  const fontsGenerator = generateFontStrings(generateDesignAssetUrl);
  const [googleFonts, customFonts] = fontsGenerator(fonts);

  if (googleFonts.length) {
    tags.push(
      <link
        key="google-fonts"
        href={buildGoogleFontsURL(googleFonts)}
        rel="stylesheet"
        type="text/css"
      />
    );
  }

  if (customFonts.length) {
    tags.push(<style key="custom-fonts">{customFonts}</style>);
  }

  return tags;
};

export default {
  buildGoogleFontsURL,
  formatFontName,
  generateFontStrings,
};
